import { Badge } from "@dune/ui";
import { getClientRoleLabel, getClientTypeLabel } from "@dune/utils";
import { ClientListItem } from "@dune/vars";
import { ColumnDef } from "@tanstack/react-table";

const baseColumns: ColumnDef<ClientListItem>[] = [
	{
		accessorKey: "roles",
		header: "Type",
		cell: ({ row }) => getClientRoleLabel(row.original.roles ?? ""),
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const roleA = rowA.original.roles ?? "";
			const roleB = rowB.original.roles ?? "";
			return roleA.localeCompare(roleB);
		},
	},
	{
		accessorKey: "ref",
		header: "Ref",
		cell: ({ row }) =>
			`A-${row.original.id.toString().padStart(3, "0")}`,
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const refA = `A-${rowA.original.id.toString().padStart(3, "0")}`;
			const refB = `A-${rowB.original.id.toString().padStart(3, "0")}`;
			return refA.localeCompare(refB);
		},
	},
	{
		accessorKey: "lastname",
		header: "Nom",
		cell: ({ row }) => row.original.lastname ?? "",
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const nameA = rowA.original.lastname ?? "";
			const nameB = rowB.original.lastname ?? "";
			return nameA.localeCompare(nameB);
		},
	},
	{
		accessorKey: "firstname",
		header: "Prénom",
		cell: ({ row }) => row.original.firstname ?? "",
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const firstnameA = rowA.original.firstname ?? "";
			const firstnameB = rowB.original.firstname ?? "";
			return firstnameA.localeCompare(firstnameB);
		},
	},
	{
		accessorKey: "denomination",
		header: "Raison Sociale",
		cell: ({ row }) => {
			const companyName = row.original.denomination;
			return companyName ? (
				<div className="truncate max-w-[200px]" title={companyName}>
					{companyName}
				</div>
			) : (
				<span className="text-gray-400">—</span>
			);
		},
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const companyA = rowA.original.denomination ?? "";
			const companyB = rowB.original.denomination ?? "";
			return companyA.localeCompare(companyB);
		},
	},
	{
		accessorKey: "clientType",
		header: "Forme juridique",
		cell: ({ row }) => getClientTypeLabel(row.original.clientType ?? ""),
		enableSorting: true,
		sortingFn: (rowA, rowB) => {
			const typeA = rowA.original.clientType ?? "";
			const typeB = rowB.original.clientType ?? "";
			return typeA.localeCompare(typeB);
		},
	},
	{
		accessorKey: "allConstructionSites",
		header: "Chantiers",
		cell: ({ row }) => {
			const constructionSites = row.original.allConstructionSites || [];

			if (constructionSites.length === 0) {
				return <span className="text-gray-500">Aucun chantier</span>;
			}

			const maxDisplay = 3;
			const displaySites = constructionSites.slice(0, maxDisplay);
			const hasMore = constructionSites.length > maxDisplay;

			return (
				<div className="flex flex-wrap gap-1">
					{displaySites.map((site) => (
						<Badge key={site.id} variant="secondary" className="text-xs">
							{site.ref}
						</Badge>
					))}
					{hasMore && (
						<Badge variant="outline" className="text-xs">
							+{constructionSites.length - maxDisplay}
						</Badge>
					)}
				</div>
			);
		},
		enableSorting: false,
	},
];

const apporteursColumn: ColumnDef<ClientListItem> = {
	accessorKey: "brokerName",
	header: "Apporteur",
	cell: ({ row }) => row.original.brokerName ?? "",
	enableSorting: true,
	sortingFn: (rowA, rowB) => {
		const brokerA = rowA.original.brokerName ?? "";
		const brokerB = rowB.original.brokerName ?? "";
		return brokerA.localeCompare(brokerB);
	},
};

export const getDuneColumns = (): ColumnDef<ClientListItem>[] => {
	const columns = [...baseColumns];
	columns.splice(-1, 0, apporteursColumn);
	return columns;
};

export const getBrokerColumns = (): ColumnDef<ClientListItem>[] => {
	return baseColumns;
};

export const clientColumns = baseColumns;
